import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import './VoiceChat.css';

const VoiceChat = forwardRef(({ onSendMessage, isOpen }, ref) => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const isInitializedRef = useRef(false);
  const [voiceStatus, setVoiceStatus] = useState('Click microphone to start');
  const speechSynthesisRef = useRef(window.speechSynthesis);
  const currentUtteranceRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (!isInitializedRef.current && 'webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = false; // Changed to false for better stability
      recognition.lang = 'en-US'; // Set language explicitly
      
      recognition.onstart = () => {
        setIsListening(true);
        setVoiceStatus('Listening...');
      };

      recognition.onresult = (event) => {
        const transcript = event.results[event.results.length - 1][0].transcript;
        
        // Stop AI's speech when user starts talking
        if (isSpeaking) {
          stopSpeaking();
        }
        
        setVoiceStatus('Processing...');
        onSendMessage(transcript);
      };

      recognition.onerror = (event) => {
        if (event.error !== 'no-speech') {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          setVoiceStatus('Error occurred. Click to try again.');
        }
      };

      recognition.onend = () => {
        // Restart recognition if we're still in listening mode
        if (isListening) {
          try {
            recognition.start();
          } catch (error) {
            console.error('Error restarting recognition:', error);
          }
        } else {
          setVoiceStatus('Click microphone to start');
        }
      };

      recognitionRef.current = recognition;
      isInitializedRef.current = true;
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error('Error stopping recognition:', error);
        }
      }
      stopSpeaking();
    };
  }, [isListening, onSendMessage]);

  const startListening = () => {
    if (!recognitionRef.current || isListening) return;

    try {
      // Stop any ongoing speech when starting to listen
      stopSpeaking();
      recognitionRef.current.start();
      setIsListening(true);
      setVoiceStatus('Listening...');
    } catch (error) {
      console.error('Error starting recognition:', error);
      setVoiceStatus('Error occurred. Try again.');
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current || !isListening) return;

    try {
      setIsListening(false);
      recognitionRef.current.stop();
      setVoiceStatus('Click microphone to start');
    } catch (error) {
      console.error('Error stopping recognition:', error);
    }
  };

  const speak = async (text) => {
    if (!text) return;

    try {
      // Cancel any ongoing speech
      stopSpeaking();

      const utterance = new SpeechSynthesisUtterance(text);
      currentUtteranceRef.current = utterance;

      // Get available voices and select preferred voice
      const voices = speechSynthesisRef.current.getVoices();
      const preferredVoice = voices.find(
        voice => voice.name === 'Google UK English Female' ||
                voice.name === 'Microsoft Aria Online (Natural) - English (United Kingdom)' ||
                voice.name === 'Samantha'
      ) || voices.find(
        voice => voice.lang === 'en-GB' || voice.lang === 'en-US'
      );

      if (preferredVoice) {
        utterance.voice = preferredVoice;
        utterance.lang = preferredVoice.lang;
      }

      // Configure speech parameters for more natural sound
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      // Process text for better flow
      const sentences = text.split(/([.!?]+)\s+/)
        .filter(sentence => sentence.trim().length > 0)
        .map(sentence => sentence.trim());

      utterance.text = sentences.join(' ');

      utterance.onstart = () => {
        setIsSpeaking(true);
        setVoiceStatus('Speaking...');
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        currentUtteranceRef.current = null;
        setVoiceStatus('Listening...');
      };

      utterance.onerror = (event) => {
        if (event.error !== 'interrupted') {
          console.error('Speech synthesis error:', event);
          setVoiceStatus('Error in speech. Still listening...');
        }
        setIsSpeaking(false);
        currentUtteranceRef.current = null;
      };

      // Add a small delay before speaking for more natural interaction
      await new Promise(resolve => setTimeout(resolve, 100));
      speechSynthesisRef.current.speak(utterance);

    } catch (error) {
      console.error('Error in speak:', error);
      setVoiceStatus('Error in speech. Still listening...');
    }
  };

  const stopSpeaking = () => {
    try {
      speechSynthesisRef.current.cancel();
      if (currentUtteranceRef.current) {
        currentUtteranceRef.current = null;
      }
      setIsSpeaking(false);
    } catch (error) {
      console.error('Error stopping speech:', error);
    }
  };

  useImperativeHandle(ref, () => ({
    speak,
    stopSpeaking
  }));

  return (
    <div className={`voice-interface ${isOpen ? 'open' : ''}`}>
      <div className="voice-visualization">
        <div className={`voice-circle ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''}`}>
          <div className="voice-waves"></div>
          <button
            className="voice-button"
            onClick={isListening ? stopListening : startListening}
            title={isListening ? 'Stop listening' : 'Start listening'}
          >
            <i className={`fas ${isListening ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
          </button>
        </div>
      </div>
      <div className="voice-status">
        {voiceStatus}
      </div>
    </div>
  );
});

export default VoiceChat;